/**
 * profileChange helpers — BACKLOG-1675 (Bill 2026-10-02).
 *
 * Shared by the RAO People step (Person Dialog save on a cart) and RCM Edit
 * CIF. Maps a RaoPerson onto the wire input the server diffs against live Core
 * Director, reads the admin-config mode, and stamps the applied result on the
 * person so the commit pipeline does not repeat the CIF update.
 *
 * The input mapping MUST mirror CustomerProfileChangePlanner.InputFromCartPerson
 * on the server (same field names, same phoneCell ?? phone fallback, same
 * useMailingAddr read) — the commit-time skip compares the key the server
 * returned at apply with the key it recomputes from the cart person.
 */
import type {
  ProfileChangeApplyResponse,
  ProfileChangeMode,
  ProfileChangePersonInput,
} from '@retail-ops/shared-types';
import { PROFILE_CHANGE_MODE_KEY } from '@retail-ops/shared-types';
import type { RaoPerson } from '@/modules/RAO/types';

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim().length > 0 ? v : undefined;
}

export function personToProfileChangeInput(person: RaoPerson): ProfileChangePersonInput {
  const extra = (person.extra ?? {}) as Record<string, unknown>;
  const loose = person as unknown as Record<string, unknown>;
  const useMailing = typeof extra['useMailingAddr'] === 'boolean' ? (extra['useMailingAddr'] as boolean) : undefined;
  const phoneCell = str(person.phoneCell) ?? str(loose['phone']);
  const input: ProfileChangePersonInput = {
    ...(str(person.firstName) ? { firstName: person.firstName } : {}),
    ...(str(person.middleName) ? { middleName: person.middleName } : {}),
    ...(str(person.lastName) ? { lastName: person.lastName } : {}),
    ...(str(person.suffix) ? { suffix: person.suffix } : {}),
    ...(str(person.dob) ? { dob: person.dob } : {}),
    ...(str(person.addr) ? { addr: person.addr } : {}),
    ...(str(person.city) ? { city: person.city } : {}),
    ...(str(person.state) ? { state: person.state } : {}),
    ...(str(person.zip) ? { zip: person.zip } : {}),
    ...(str(person.mailAddr) ? { mailAddr: person.mailAddr } : {}),
    ...(str(person.mailCity) ? { mailCity: person.mailCity } : {}),
    ...(str(person.mailState) ? { mailState: person.mailState } : {}),
    ...(str(person.mailZip) ? { mailZip: person.mailZip } : {}),
    ...(useMailing !== undefined ? { useMailingAddr: useMailing } : {}),
    ...(str(person.phoneHome) ? { phoneHome: person.phoneHome } : {}),
    ...(phoneCell ? { phoneCell } : {}),
    ...(str(person.phoneBus) ? { phoneBus: person.phoneBus } : {}),
    ...(str(person.email) ? { email: person.email } : {}),
    ...(str(person.employer) ? { employer: person.employer } : {}),
    ...(str(person.occupation) ? { occupation: person.occupation } : {}),
  };
  return input;
}

/** `profileChange.mode` from the config bundle; the server default is confirm. */
export function readProfileChangeMode(systemSettings: unknown): ProfileChangeMode {
  const probe = (v: unknown): ProfileChangeMode | null => {
    if (typeof v !== 'string') return null;
    const s = v.trim().toLowerCase();
    return s === 'confirm' || s === 'commit-time' ? (s as ProfileChangeMode) : null;
  };
  if (Array.isArray(systemSettings)) {
    for (const row of systemSettings as ReadonlyArray<Record<string, unknown>>) {
      if (!row || typeof row !== 'object') continue;
      const k = typeof row['settingKey'] === 'string' ? row['settingKey'] : typeof row['key'] === 'string' ? row['key'] : undefined;
      if (k === PROFILE_CHANGE_MODE_KEY) {
        const m = probe(row['settingValue'] ?? row['value']);
        if (m) return m;
      }
    }
  } else if (systemSettings && typeof systemSettings === 'object') {
    const m = probe((systemSettings as Record<string, unknown>)[PROFILE_CHANGE_MODE_KEY]);
    if (m) return m;
  }
  return 'confirm';
}

/** Record on the person that Core Director already holds these values. */
export function stampProfileChange(
  person: RaoPerson,
  resp: ProfileChangeApplyResponse,
  authMethodCode: string,
): RaoPerson {
  const extra = (person.extra ?? {}) as Record<string, unknown>;
  return {
    ...person,
    extra: {
      ...extra,
      profileChange: {
        appliedAt: new Date().toISOString(),
        appliedKey: resp.appliedKey,
        maintActionId: resp.maintActionId ?? null,
        authMethod: authMethodCode,
      },
    },
  };
}

/** One-line result for the banner after an apply. */
export function summarizeApply(resp: ProfileChangeApplyResponse): { tone: 'ok' | 'warn'; text: string } {
  const okAccts = resp.accounts.filter((a) => a.ok).length;
  const failedAccts = resp.accounts.filter((a) => !a.ok);
  const failedNotes = resp.notes.filter((n) => !n.ok).length;
  const unverified = resp.unverifiedFields ?? [];
  // Never claim the customer record changed unless Core Director was read back and agrees.
  const customerLine =
    resp.custModVerified === true
      ? 'Customer record updated and confirmed in Core Director'
      : unverified.length > 0
        ? `Core Director accepted the update, but ${unverified.join(', ')} still ${unverified.length === 1 ? 'shows' : 'show'} the old value — check the customer in Core Director`
        : 'Customer update sent to Core Director, but it could not be confirmed — check the customer in Core Director';
  const parts: string[] = [customerLine];
  if (okAccts > 0) parts.push(`${okAccts} account${okAccts === 1 ? '' : 's'} updated`);
  const base = (resp.replayed ? 'Already saved a moment ago. ' : '') + parts.join('; ') + '.';
  const customerOk = resp.custModVerified === true;
  if (customerOk && failedAccts.length === 0 && failedNotes === 0) return { tone: 'ok', text: base };
  if (failedAccts.length === 0 && failedNotes === 0) return { tone: 'warn', text: base };
  const problems: string[] = [];
  for (const a of failedAccts) problems.push(`account ${a.acctId}: ${a.error ?? 'not updated'}`);
  if (failedNotes > 0) problems.push(`${failedNotes} note${failedNotes === 1 ? '' : 's'} not written`);
  return { tone: 'warn', text: `${base} Needs attention — ${problems.join('; ')}.` };
}
